(() => {
  const SECTION_ID = 'account-panel';
  const FETCH_TIMEOUT_MS = 15000;

  const PASSWORD_CHECKS = {
    length: (value) => value.length >= 8,
    number: (value) => /\d/.test(value),
    special: (value) => /[^A-Za-z0-9\s]/.test(value),
  };

  const isPasswordValid = (value) =>
    Object.keys(PASSWORD_CHECKS).every((key) => PASSWORD_CHECKS[key](value));

  const getModule = () => document.querySelector('[mdle_name="module_5"]');

  const getContainer = () =>
    getModule()?.querySelector('[data-account-panel-content]');

  const fetchWithTimeout = (url, options = {}) => {
    const controller = new AbortController();
    const timeoutId = window.setTimeout(
      () => controller.abort(),
      FETCH_TIMEOUT_MS
    );

    return fetch(url, { ...options, signal: controller.signal }).finally(() => {
      window.clearTimeout(timeoutId);
    });
  };

  const showPanelError = (root, message) => {
    if (!root || !message) return;

    let errorEl = root.querySelector('[data-account-panel-error]');

    if (!errorEl) {
      errorEl = document.createElement('p');
      errorEl.className = 'form__message';
      errorEl.setAttribute('data-account-panel-error', '');
      root.prepend(errorEl);
    }

    errorEl.hidden = false;
    errorEl.textContent = message;
  };

  const clearPanelError = (root) => {
    root?.querySelector('[data-account-panel-error]')?.remove();
  };

  const showView = (root, viewName) => {
    if (!root) return;

    root.querySelectorAll('[data-account-view]').forEach((view) => {
      view.hidden = view.dataset.accountView !== viewName;
    });

    root.dataset.activeView = viewName;
    wirePanelCaptchas(root);
  };

  const wirePanelCaptchas = (root) => {
    if (!root || !window.Shopify?.captcha?.protect) return;

    root.querySelectorAll('form[data-shopify-captcha]').forEach((form) => {
      if (form.dataset.captchaWired === 'true') return;
      window.Shopify.captcha.protect(form);
      form.dataset.captchaWired = 'true';
    });
  };

  const initPasswordValidation = (root) => {
    const form = root.querySelector('[id$="-RegisterForm"]');
    const passwordInput = root.querySelector('[id$="-RegisterPassword"]');
    const submitButton = form?.querySelector('[type="submit"]');
    const requirements = root.querySelectorAll('[data-password-requirement]');

    if (!form || !passwordInput || !requirements.length) return;
    if (form.dataset.passwordValidationBound === 'true') return;
    form.dataset.passwordValidationBound = 'true';

    const updateRequirements = () => {
      const value = passwordInput.value;
      const isValid = isPasswordValid(value);

      requirements.forEach((item) => {
        const key = item.dataset.passwordRequirement;
        item.classList.toggle(
          'login-custom__password-requirement--met',
          PASSWORD_CHECKS[key](value)
        );
      });

      if (submitButton) {
        submitButton.disabled = !isValid;
        submitButton.setAttribute('aria-disabled', String(!isValid));
      }
    };

    passwordInput.addEventListener('input', updateRequirements);
    updateRequirements();

    form.addEventListener('submit', (event) => {
      if (isPasswordValid(passwordInput.value)) return;

      event.preventDefault();
      passwordInput.focus();
      updateRequirements();
    });
  };

  const bindViewTriggers = (root) => {
    root.querySelectorAll('[data-account-view-trigger]').forEach((trigger) => {
      if (trigger.dataset.accountViewBound === 'true') return;
      trigger.dataset.accountViewBound = 'true';

      trigger.addEventListener('click', (event) => {
        event.preventDefault();
        clearPanelError(root);
        showView(root, trigger.dataset.accountViewTrigger);
      });
    });
  };

  const loadAccountPanel = async (fetchUrl, preferredView = 'login') => {
    const container = getContainer();
    if (!container) return false;

    container.classList.add('is-loading');

    try {
      const response = await fetchWithTimeout(fetchUrl, {
        credentials: 'same-origin',
        headers: {
          Accept: 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('No se pudo cargar el panel de cuenta.');
      }

      const contentType = response.headers.get('content-type') || '';

      if (!contentType.includes('application/json')) {
        throw new Error('Respuesta inválida del servidor.');
      }

      const data = await response.json();
      const html = data[SECTION_ID];

      if (!html) {
        throw new Error('No se recibió contenido del panel.');
      }

      container.innerHTML = html;
      const panel = container.querySelector('[data-account-panel]');
      initAccountPanel(panel, preferredView);
      window.dispatchEvent(new CustomEvent('header-module:content-change'));
      return true;
    } catch (error) {
      console.error('[account-panel]', error);
      return false;
    } finally {
      container.classList.remove('is-loading');
    }
  };

  const initAccountPanel = (root, preferredView = 'login') => {
    if (!root) return;

    showView(root, preferredView);
    bindViewTriggers(root);
    initPasswordValidation(root);
    wirePanelCaptchas(root);
  };

  const setupAccountPanel = () => {
    const container = getContainer();
    if (!container) return;

    if (container.querySelector('.account-panel-menu')) return;

    const panel = container.querySelector('[data-account-panel]');
    if (panel) {
      initAccountPanel(panel, panel.dataset.activeView || 'login');
      return;
    }

    loadAccountPanel('/account/login?sections=' + SECTION_ID, 'login');
  };

  const observeModule = () => {
    const module = getModule();
    if (!module) return;

    const maybeEnsurePanel = () => {
      if (!module.classList.contains('activate')) return;
      const container = getContainer();
      if (container?.querySelector('[data-account-panel]')) return;
      if (container?.querySelector('.account-panel-menu')) return;
      loadAccountPanel('/account/login?sections=' + SECTION_ID, 'login');
    };

    const observer = new MutationObserver(maybeEnsurePanel);
    observer.observe(module, { attributes: true, attributeFilter: ['class'] });
    maybeEnsurePanel();
  };

  const boot = () => {
    setupAccountPanel();
    observeModule();
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }
})();
